export type SmmRouteKey = "dashboard" | "members" | "reports";

export const SMM_ROUTE_PATHS = {
  dashboard: "/crm/smm",
  members: "/crm/smm/members",
  reports: "/crm/smm/reports",
} as const satisfies Record<SmmRouteKey, string>;

const SMM_ROUTE_ALIASES: Record<string, SmmRouteKey> = {
  "/crm/smm": "dashboard",
  "/crm/smm/": "dashboard",
  "/crm/smm/dashboard": "dashboard",
  "/crm/smm/home": "dashboard",

  "/crm/smm/members": "members",
  "/crm/smm/members/": "members",
  "/crm/smm/member": "members",
  "/crm/smm/member/": "members",
  "/crm/smm/team": "members",
  "/crm/smm/team/": "members",
  "/crm/smm/roster": "members",
  "/crm/smm/roster/": "members",

  "/crm/smm/reports": "reports",
  "/crm/smm/reports/": "reports",
  "/crm/smm/report": "reports",
  "/crm/smm/report/": "reports",
  "/crm/smm/performance": "reports",
  "/crm/smm/performance/": "reports",
};

export type SmmRouteValidation = {
  ok: boolean;
  input: string;
  output: string;
  corrected: boolean;
  routeKey?: SmmRouteKey;
  reason?: string;
};

function cleanPath(input: string) {
  const [withoutHash] = input.split("#");
  const [path] = withoutHash.split("?");
  return path || "/";
}

export function validateSmmHref(href: string): SmmRouteValidation {
  const path = cleanPath(href);
  const routeKey = SMM_ROUTE_ALIASES[path];

  if (!path.startsWith("/crm/smm")) {
    return {
      ok: true,
      input: href,
      output: href,
      corrected: false,
    };
  }

  if (!routeKey) {
    return {
      ok: false,
      input: href,
      output: SMM_ROUTE_PATHS.dashboard,
      corrected: true,
      reason: `Unknown SMM route "${path}"; corrected to dashboard.`,
    };
  }

  const canonical = SMM_ROUTE_PATHS[routeKey];
  return {
    ok: true,
    input: href,
    output: canonical,
    corrected: path !== canonical,
    routeKey,
    reason: path !== canonical ? `SMM route alias "${path}" corrected to "${canonical}".` : undefined,
  };
}

export function getSmmNotFoundFallback(pathname: string) {
  const validation = validateSmmHref(pathname);
  if (!pathname.startsWith("/crm/smm")) return null;

  if (validation.corrected && validation.routeKey) {
    return {
      target: validation.output,
      label: validation.routeKey,
      reason: validation.reason ?? "SMM route alias corrected.",
    };
  }

  // If the canonical child route itself reaches the global 404, the route tree
  // is stale or the nested SMM route failed to mount. Route through the known
  // working SMM index and let it render the requested section as a recovery.
  if (pathname === SMM_ROUTE_PATHS.members) {
    return {
      target: `${SMM_ROUTE_PATHS.dashboard}?view=members&routeRecovery=1`,
      label: "members" as const,
      reason: "Canonical SMM Members route did not match; using the SMM index fallback.",
    };
  }
  if (pathname === SMM_ROUTE_PATHS.reports) {
    return {
      target: `${SMM_ROUTE_PATHS.dashboard}?view=reports&routeRecovery=1`,
      label: "reports" as const,
      reason: "Canonical SMM Reports route did not match; using the SMM index fallback.",
    };
  }

  return null;
}

export function expectedSmmRouteChain(pathname: string, search?: Record<string, unknown>) {
  const view = search?.view;
  if (pathname === SMM_ROUTE_PATHS.dashboard && view === "members") {
    return ["/crm", "/crm/smm", "/crm/smm/", "fallback:view=members"];
  }
  if (pathname === SMM_ROUTE_PATHS.dashboard && view === "reports") {
    return ["/crm", "/crm/smm", "/crm/smm/", "fallback:view=reports"];
  }
  if (pathname.startsWith(SMM_ROUTE_PATHS.members)) {
    return ["/crm", "/crm/smm", "/crm/smm/members"];
  }
  if (pathname.startsWith(SMM_ROUTE_PATHS.reports)) {
    return ["/crm", "/crm/smm", "/crm/smm/reports"];
  }
  if (pathname === SMM_ROUTE_PATHS.dashboard || pathname.startsWith(`${SMM_ROUTE_PATHS.dashboard}/`)) {
    return ["/crm", "/crm/smm", "/crm/smm/"];
  }
  return null;
}